/**
 * /shop/ saved filter sets — localStorage-backed shortcuts for power
 * users who repeatedly apply the same filter combinations.
 *
 * Storage shape (localStorage.txShopSavedSets, JSON-encoded):
 *   [
 *     {
 *       id:     'set-…',
 *       name:   'Hazardous-area cable glands',
 *       params: 'cat[]=sealcon-hazardous&material[]=nylon&ip[]=IP68%20(10%20bar)',
 *       digest: ['SEALCON HAZARDOUS', 'NYLON', 'IP68 (10 BAR)']
 *     }, ...
 *   ]
 *
 * params is the URL query string the shop-ajax driver can replay
 * verbatim. digest is the human-readable label trio shown in the
 * rail so users don't have to remember what each set contains.
 *
 * Apply a set → dispatches a `shop:navigate` event on `document`
 * with the saved URL; shop-ajax listens for it and re-applies the
 * filters (form sync + history push + AJAX fetch).
 */
const STORAGE_KEY = 'txShopSavedSets';
const MAX_DIGEST  = 3;
const MAX_SETS    = 20;
const MAX_NAME    = 60;

// CSS.escape polyfill — needed because we build selectors from
// user-controlled values like "M16 x 1.5" (spaces) or "IP68 (10 bar)"
// (parens). CSS.escape is on every browser we target (Chrome 41+,
// Safari 10.1+); fallback is defensive in case localStorage outlives
// a browser-engine change.
const cssEscape = (s) =>
    window.CSS && CSS.escape
        ? CSS.escape(s)
        : String(s).replace(/[^a-zA-Z0-9_-]/g, '\\$&');

const loadSets = () => {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        const arr = raw ? JSON.parse(raw) : [];
        return Array.isArray(arr) ? arr : [];
    } catch (e) {
        // Quota exceeded, JSON parse error, or localStorage disabled
        // (Safari private mode). Fail soft — feature is non-critical.
        return [];
    }
};

const saveSets = (sets) => {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(sets));
        return true;
    } catch (e) {
        return false;
    }
};

const newId = () =>
    'set-' + Math.random().toString(36).slice(2, 10) + '-' + Date.now().toString(36);

const init = () => {
    const widget = document.querySelector('[data-shop-saved-sets]');
    if (!widget) return;

    const listEl  = widget.querySelector('[data-shop-saved-list]');
    const saveBtn = widget.querySelector('[data-shop-saved-save]');
    const form    = document.querySelector('[data-shop-filter-form]');
    if (!listEl || !saveBtn || !form) return;

    /**
     * Snapshot the form's currently-active filters into a URL query
     * string + a human-readable digest. The digest pulls label text
     * from each checkbox's surrounding <label>, so saved sets show
     * "SEALCON HAZARDOUS" instead of slugs.
     *
     * Price min/max and search query are intentionally excluded from
     * the digest (they don't compress to short labels) but are
     * included in the params so the saved URL still re-applies them.
     * A pressure range digests as one "300–500 PSI" label. Values a
     * filter change leaves out of the URL (shop-ajax.js urlFromForm)
     * stay out of a set too: price at the catalog's bounds, a toolbar
     * select on its first option.
     */
    const priceWrap = form.querySelector('[data-shop-price-range]');
    const atDefault = (name, value) => {
        if (name === 'price_min') return !!priceWrap && Number(value) === Number(priceWrap.dataset.priceFloor);
        if (name === 'price_max') return !!priceWrap && Number(value) === Number(priceWrap.dataset.priceCeiling);
        const select = document.querySelector(`select[data-shop-toolbar-select][name="${cssEscape(name)}"]`);
        return !!select && value === select.options[0]?.value;
    };

    const snapshotCurrent = () => {
        const fd = new FormData(form);
        const params = new URLSearchParams();
        const labels = [];

        for (const [name, value] of fd.entries()) {
            if (value === '' || value == null || atDefault(name, value)) continue;
            params.append(name, value);

            if (labels.length >= MAX_DIGEST) continue;
            if (name === 'q' || name === 'price_min' || name === 'price_max' || name === 'prmax') continue;
            if (name === 'prmin') {
                const psi = (v) => Number(v).toLocaleString('en-US');
                labels.push(`${psi(value)}–${psi(fd.get('prmax'))} PSI`);
                continue;
            }

            const input = form.querySelector(
                `input[name="${cssEscape(name)}"][value="${cssEscape(value)}"]`
            );
            const labelEl = input?.closest('label')?.querySelector(
                '.tx-shop__check-label, .tx-shop__filter-pill-label'
            );
            if (labelEl) {
                labels.push(labelEl.textContent.trim().toUpperCase());
            } else if (input?.type === 'hidden') {
                // Archive-scope inputs (ptag[] on /product-tag/ pages)
                // have no rail label — digest the raw slug so a saved
                // set never reads "No filter values" while silently
                // carrying a scope.
                labels.push(value.toUpperCase());
            }
        }

        return { paramsStr: params.toString(), digest: labels };
    };

    const render = () => {
        const sets = loadSets();
        listEl.innerHTML = '';
        if (!sets.length) {
            // Zero state: hint copy beats an unexplained empty area
            // for first-time users encountering the widget.
            const hint = document.createElement('li');
            hint.className = 'tx-shop__saved-empty';
            hint.textContent = 'No saved sets yet. Apply some filters, then click save to keep them one click away.';
            listEl.hidden = false;
            listEl.appendChild(hint);
            return;
        }
        listEl.hidden = false;
        for (const set of sets) {
            const li = document.createElement('li');
            li.className = 'tx-shop__saved-item';
            li.dataset.setId = set.id;
            li.innerHTML = `
                <button type="button" class="tx-shop__saved-apply" data-shop-saved-apply>
                    <span class="tx-shop__saved-name"></span>
                    <span class="mono tx-shop__saved-digest"></span>
                </button>
                <button type="button" class="tx-shop__saved-delete"
                        data-shop-saved-delete aria-label="Delete set">&times;</button>
            `;
            // Set text content via .textContent to avoid HTML
            // injection from user-controlled names / digests.
            li.querySelector('.tx-shop__saved-name').textContent = set.name;
            li.querySelector('.tx-shop__saved-digest').textContent =
                (set.digest && set.digest.length) ? set.digest.join(' · ') : 'No filter values';
            listEl.appendChild(li);
        }
    };

    saveBtn.addEventListener('click', () => {
        const snap = snapshotCurrent();
        if (!snap.paramsStr) {
            // Nothing active — saving the empty/default state isn't
            // useful. Give the user feedback rather than silently
            // creating an empty set.
            window.alert(
                'Apply at least one filter before saving — there are no active filters to capture.'
            );
            return;
        }

        const sets = loadSets();
        if (sets.length >= MAX_SETS) {
            // Refuse rather than silently dropping the oldest — losing
            // a saved set mid-session without warning is the kind of
            // data loss that violates user trust.
            window.alert(
                `You have ${MAX_SETS} saved sets, the maximum. Delete one before saving another.`
            );
            return;
        }

        const raw = window.prompt('Name this filter set:', '') || '';
        const name = raw.trim().slice(0, MAX_NAME);
        if (!name) return;

        sets.unshift({
            id:     newId(),
            name,
            params: snap.paramsStr,
            digest: snap.digest,
        });
        if (!saveSets(sets)) {
            window.alert(
                'Could not save — your browser may have storage disabled or full.'
            );
            return;
        }
        render();
    });

    listEl.addEventListener('click', (e) => {
        const li = e.target.closest('.tx-shop__saved-item');
        if (!li) return;
        const id = li.dataset.setId;

        if (e.target.closest('[data-shop-saved-delete]')) {
            e.preventDefault();
            const sets = loadSets().filter((s) => s.id !== id);
            saveSets(sets);
            render();
            return;
        }

        if (e.target.closest('[data-shop-saved-apply]')) {
            e.preventDefault();
            const set = loadSets().find((s) => s.id === id);
            if (!set) return;
            const action = form.getAttribute('action') || location.pathname;
            const url    = action + (set.params ? '?' + set.params : '');
            document.dispatchEvent(new CustomEvent('shop:navigate', {
                detail: { url },
            }));
        }
    });

    // Multi-tab sync — if another tab edits / deletes a set, the
    // `storage` event fires here. Re-rendering picks up the change
    // so the user doesn't apply a set that was deleted elsewhere.
    window.addEventListener('storage', (e) => {
        if (e.key === STORAGE_KEY) render();
    });

    render();
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
